<template>
  <section class="py-[50px] md:py-24">
    <div class="flex flex-col gap-16 items-center px-8 max-w-7xl mx-auto">
      <!-- Header -->
      <div class="flex flex-col gap-5 items-center max-w-3xl text-center w-full">
        <div class="flex flex-col gap-3 items-start w-full">
          <p v-if="tag" class="text-base font-semibold leading-6 text-grey-dark-300 w-full">{{ tag }}</p>
          <h2
            class="text-3xl md:text-4xl font-semibold leading-[38px] md:leading-11 text-grey-dark-50 w-full flex items-center justify-center gap-3 flex-wrap"
          >
            <template v-if="titleImage">
              <span v-for="(word, index) in title.split(' ')" :key="index">
                <img
                  v-if="word.toLowerCase() === 'layup'"
                  :src="getTitleImage()"
                  alt="LayUp Boost"
                  class="inline-block h-8 md:h-10"
                />
                <span v-else>{{ word }}</span>
              </span>
            </template>
            <template v-else>
              {{ title }}
            </template>
          </h2>
        </div>
        <p class="text-base md:text-xl font-normal leading-6 md:leading-[30px] text-grey-dark-300 w-full">
          {{ description }}
        </p>
      </div>

      <!-- Desktop Grid / Mobile Carousel -->
      <div class="w-full">
        <!-- Desktop Grid (hidden on mobile) -->
        <div class="hidden md:flex gap-8 w-full justify-center flex-wrap">
          <slot name="card" v-for="(item, index) in items" :key="index" :item="item" :index="index"></slot>
        </div>

        <!-- Mobile Carousel (visible only on mobile) -->
        <div class="md:hidden">
          <Carousel
            class="w-full"
            :opts="{
              align: 'center',
              loop: false,
            }"
          >
            <CarouselContent class="-ml-6">
              <CarouselItem v-for="(item, index) in items" :key="index" class="pl-6 basis-full">
                <slot name="card" :item="item" :index="index"></slot>
              </CarouselItem>
            </CarouselContent>
            <CarouselDotButtons class="mt-8" selected-class="border-brand-700 bg-brand-700" />
          </Carousel>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts" generic="T">
import { Carousel, CarouselContent, CarouselDotButtons, CarouselItem } from '@/components/ui/carousel';
import { addBreadcrumb, createWarning } from '@/utils/monitoring.utils';

interface Props {
  tag?: string;
  titleImage?: string;
  title: string;
  description: string;
  items: T[];
}

const props = defineProps<Props>();

const getTitleImage = () => {
  if (!props.titleImage) return '';
  try {
    return new URL(`../../assets/images/${props.titleImage}`, import.meta.url).href;
  } catch (e) {
    addBreadcrumb('image.load', `Failed to load image ${props.titleImage}`);
    createWarning(e);
    return '';
  }
};
</script>
